/* eslint-disable no-prototype-builtins */
import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'

import axios from 'axios'
import { Link } from 'react-router-dom'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'

import { viewHingePromptItem } from '../profile/view/hinge/jsx/viewHingePromptItemJSX'
import { viewHingeVitalInfoItem } from '../profile/view/hinge/jsx/viewHingeVitalInfoItemJSX'
import { statsJSX } from '../user/reviewerBio/helpers/statsJSX'

import useWindowDimensions from '../../helpers/windowDimensions'
import { getPayload, userIsAuthenticated, getTokenFromLocalStorage } from '../../helpers/storage'
import { seoPageTags } from '../../helpers/analytics'
import { standardSpinner } from '../../helpers/spinners'
import { navbarHeight, hingeProfileBackgroundColor, dashboardNavbarHeight, footerHeight, positionChangeWidthSm, hingeProfileImageDimension } from '../../helpers/variableDefaults'
import { makeTabIndex } from '../../helpers/formatting'
import { standardErrorContainer } from '../../helpers/errors'
import { saveSuccessfulTypography, advisoryTypography } from '../../helpers/typographies'
import { standardButton } from '../../helpers/buttons'


// Reviewer Profile Page
const ReviewerProfile = ({ reviewerProfileObj = {}, isDemo = false, demoUser = {}, profileType = '', reviewerFirstName = '' }) => {
  
  // Window Dimensions
  const { height, width } = useWindowDimensions()

  // Navigate
  const navigate = useNavigate()

  // Params
  const { reviewerProfileId } = useParams()

  // Payload
  const payload = getPayload()

  // Dashboard Navigation
  const [reviewerProfile, setReviewerProfile] = useState(Object.keys(reviewerProfileObj).length > 0 ? reviewerProfileObj : {})

  // Loading
  const [loading, setLoading] = useState(false)

  // Errors
  const [errors, setErrors] = useState(false)

  useEffect(() => {
    if (isDemo ) {
      // console.log('reviewer profile demo')
      if ((reviewerProfile === null || Object.keys(reviewerProfile).length === 0)) {
        const reviewerProfileToSet = demoUser.datingProfiles
          .filter(profile => profile.type === profileType)[0].profileReviews
          .filter(review => review.reviewerProfile.bio.vitalInfo.firstName.toLowerCase().replace(/ /g,'') === reviewerFirstName.toLowerCase().replace(/ /g,''))[0].reviewerProfile
        // console.log('reviewerProfileToSet ->', reviewerProfileToSet)
        setReviewerProfile({ ...reviewerProfileToSet })
      }
    } else if (!userIsAuthenticated()) {

      navigate(`/login`)

    } else {
      const fetchData = async () => {
        setLoading(true)
        //   // console.log('userId ->', userId)
        try {
          const { data: retrievedReviewerProfile } = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/reviewer-profiles/${reviewerProfileId}/no-profile-reviews/`, {
            headers: {
              Authorization: `Bearer ${getTokenFromLocalStorage()}`,
            },
          })

          // console.log('retrievedReviewerProfile ->', retrievedReviewerProfile)

          if (
            retrievedReviewerProfile._id === reviewerProfileId
            && retrievedReviewerProfile !== null
            && Object.keys(retrievedReviewerProfile).length > 0
            && 'bio' in retrievedReviewerProfile
          ) {
            // console.log('states should set')
            // Set User State
            setReviewerProfile(retrievedReviewerProfile)

            setLoading(false)
            setErrors(false)
            // console.log('no longer loading')

          } else {
            console.log('states should not set')
            useNavigate(`/dashboard/${payload.sub}`)
            setLoading(false)
          }

        } catch (err) {
          // console.log(err)

          navigate('/')
          setLoading(false)
          setErrors(true)
        }
      }
  
      if (reviewerProfile === null || Object.keys(reviewerProfile).length === 0){
        fetchData()
      }
    }

  }, [])

  // useEffect(() => {
  //   // console.log('useEffect Runs')

  // }, [loading])

  return (
    <>
      {/* Helmet — for analytics, seo, and page title changing */}
      {seoPageTags(
        'Reviewer Profile'
      )}
    
      {/* Body */}
      <Box 
        sx={{ 
          mb: 4,
          width: '100%',
          minHeight: `calc(100vh - ${navbarHeight} - ${footerHeight})`,
          display: 'flex', flexDirection: 'column', justifyContent: (loading || (reviewerProfile === null || Object.keys(reviewerProfile).length === 0)) ? 'center' : 'flex-start', alignItems: 'center', 
        }}
      >
        {loading || (reviewerProfile === null || Object.keys(reviewerProfile).length === 0)
          ?
          standardSpinner()
          :
          <Box sx={{ 
            // backgroundColor: 'yellow',
            mt: 0, mb: 0, 
            width: width >= positionChangeWidthSm ? '90vw' : '100vw',  
            maxWidth: positionChangeWidthSm, 
            display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center', 
          }}>
            <Box
              sx={{
                mt: 2, mb: 8, py: 3, px: 2,
                backgroundColor: hingeProfileBackgroundColor,
                // backgroundColor: 'black',
                width: '100%', 
                // maxWidth: '600px',
                maxWidth: '400px',
                boxShadow: width >= positionChangeWidthSm ? 3 : 3, 
                borderRadius: width >= positionChangeWidthSm ? 3 : 0,
                display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center',
              }}
            >
              {/* Profile Picture */}
              <Box
                sx={{
                  mt: 1,
                  width: '100%',
                  display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'flex-start',
                }}
              >
                <Box
                  sx={{ 
                    my: 0,
                    width: width < positionChangeWidthSm ? '200px' : '200px',
                    height: width < positionChangeWidthSm ? '200px' : '200px',
                    backgroundImage: `url(${reviewerProfile.profilePicture})`,
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    boxShadow: 3, 
                    borderRadius: '50%', 
                    display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', 
                  }}
                />
              </Box>

              {/* Name and Pronouns */}
              <Box
                sx={{
                  mt: 1,
                  width: '100%',
                  display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
                }}
              >
                <Typography
                  textAlign={'center'}
                  sx={{
                    // width: '100%',
                    // backgroundColor: 'orange',
                    width: width < 280 ? '200px' : width < 350 ? '250px' : width < 450 ? '300px' : width < 600 ? '300px' : width < 800 ? '300px' : '300px',
                    fontWeight: 'bold',
                    fontSize: '18px',
                  }}
                >
                  {reviewerProfile.bio.vitalInfo.firstName}
                </Typography>

                {'pronouns' in reviewerProfile.bio.vitalInfo 
                  && reviewerProfile.bio.vitalInfo.pronouns !== 'Prefer not to say' 
                  &&
                  <Typography
                    textAlign={'center'}
                    sx={{
                      width: '100%',
                      fontStyle: 'italic',
                    }}
                  >
                    {reviewerProfile.bio.vitalInfo.pronouns}
                  </Typography>
                }
              </Box>


              {/* About Me */}
              <Box
                sx={{
                  mt: 2, py: 4, px: 2,
                  // width: width < 280 ? '200px' : width < 350 ? '250px' : width < 450 ? '300px' : width < 600 ? '300px' : width < 800 ? '300px' : '300px',
                  width: hingeProfileImageDimension(width),
                  backgroundColor: 'white',
                  // boxShadow: 3, 
                  border: 1,
                  borderColor: 'gainsboro',
                  borderRadius: '10px',
                  fontSize: '18px',
                  display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start',
                }}
              >
                {'bio' in reviewerProfile
                  && 'aboutMe' in reviewerProfile.bio
                  && reviewerProfile.bio.aboutMe.length > 0
                  ?
                  reviewerProfile.bio.aboutMe[0]
                  :
                  `(No About Me for this reviewer)`
                }
              </Box>

              {/* Prompt 1 */}
              {'bio' in reviewerProfile
                && 'prompts' in reviewerProfile.bio
                && reviewerProfile.bio.prompts.length > 0
                && reviewerProfile.bio.prompts[0].prompt !== '' 
                && reviewerProfile.bio.prompts[0].prompt !== 'No Prompt' 
                && reviewerProfile.bio.prompts[0].response !== ''
                &&
                  viewHingePromptItem(
                    false, 
                    false,
                    0, 
                    reviewerProfile, 
                    width
                  )
              }

              {/* Hobbies/Interests */}
              <Box
                sx={{
                  mt: 2, py: 2, px: 2,
                  width: hingeProfileImageDimension(width),
                  backgroundColor: 'white',
                  // boxShadow: 3, 
                  border: 1,
                  borderColor: 'gainsboro',
                  borderRadius: '10px',
                  display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start',
                }}
              >
                <Box
                  sx={{
                    // backgroundColor: 'yellow',
                    mt: 0,
                    width: '100%', 
                    display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  {'bio' in reviewerProfile && 'hobbies' in reviewerProfile.bio && reviewerProfile.bio.hobbies.length > 0 
                    ?
                    reviewerProfile.bio.hobbies.map((hobbyItem, index) => (
                      <Box
                        key={index}
                        textAlign={'center'}
                        sx={{
                          my: 1, 
                          mx: 1,
                          py: 1,
                          px: 1,
                          // width: '20%',
                          backgroundColor: 'white',
                          boxShadow: 3, borderRadius: 3,
                          border: 2,
                          borderColor: 'black',
                          fontSize: '12px',fontWeight: 'bold',
                          display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center',
                        }}
                      >
                        {hobbyItem}
                      </Box>
                    )) 
                    :
                    `(No Hobbies/Interests listed for this reviewer)`

                  }
                </Box>
              </Box>

              {/* Prompt 2 */}
              {'bio' in reviewerProfile
                && 'prompts' in reviewerProfile.bio
                && reviewerProfile.bio.prompts.length > 1
                && reviewerProfile.bio.prompts[1].prompt !== '' 
                && reviewerProfile.bio.prompts[1].prompt !== 'No Prompt' 
                && reviewerProfile.bio.prompts[1].response !== ''
                &&
                  viewHingePromptItem(
                    false, 
                    false,
                    1, 
                    reviewerProfile, 
                    width
                  )
              }

              {/* Vital Info */}
              {viewHingeVitalInfoItem(false, false, false, reviewerProfile, reviewerProfile, width)}

              {/* Prompt 3 */}
              {'bio' in reviewerProfile
                && 'prompts' in reviewerProfile.bio
                && reviewerProfile.bio.prompts.length > 2
                && reviewerProfile.bio.prompts[2].prompt !== '' 
                && reviewerProfile.bio.prompts[2].prompt !== 'No Prompt' 
                && reviewerProfile.bio.prompts[2].response !== ''
                &&
                  viewHingePromptItem(
                    false, 
                    false,
                    2, 
                    reviewerProfile, 
                    width
                  )
              }

              {/* Reviewer Stats */}
              {/* <Box
                sx={{
                  mt: 2, py: 2, px: 2,
                  width: hingeProfileImageDimension(width),
                  backgroundColor: 'lightyellow',
                  // boxShadow: 3, 
                  border: 1,
                  borderColor: 'gainsboro',
                  borderRadius: '10px',
                  display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start',
                }}
              >
                {statsJSX(reviewerProfile, width)}
              </Box> */}


            </Box>
          </Box>
        }

      </Box>
    </>
  )
}

export default ReviewerProfile